import React, { useState } from 'react';
import { Ruler, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ZONES = [
  { label: 'Chin', range: '8–10"', min: 8, max: 11 },
  { label: 'Shoulder', range: '12–14"', min: 12, max: 15 },
  { label: 'Collarbone', range: '16–18"', min: 16, max: 19 },
  { label: 'Mid-Back', range: '20–22"', min: 20, max: 23 },
  { label: 'Waist', range: '24–26"', min: 24, max: 27 },
  { label: 'Hip', range: '28–30"+', min: 28, max: 99 },
];

const LengthGuide = ({ selected, lengths = [], onSelect }) => {
  const [open, setOpen] = useState(false);
  const inches = parseInt(String(selected || '').replace(/[^0-9]/g, ''), 10);
  const pct = Math.max(0.12, Math.min(1, (inches - 6) / (30 - 6)));
  const selIdx = Math.max(0, lengths.indexOf(selected));

  return (
    <div className="mb-8" data-testid="length-guide">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="length-guide-toggle"
        className="w-full flex items-center justify-between px-5 py-3.5 border-2 border-[#c6a15b] rounded-full bg-[#faf6ef] hover:bg-[#0b0b0b] hover:border-[#0b0b0b] transition-colors group"
      >
        <span className="flex items-center gap-2 overline text-[0.7rem] text-[#a8823c] group-hover:text-[#e7d3a1] uppercase tracking-[0.15em] transition-colors">
          <Ruler size={15} className="text-[#c6a15b] group-hover:text-[#e7d3a1] transition-colors" /> Length Guide — Inches to Your Look
        </span>
        <ChevronDown size={16} className={`text-[#a8823c] group-hover:text-[#e7d3a1] transition-all duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-4 flex gap-5 bg-[#faf6ef] border border-[#eadfca] p-4">
              {/* Visual figure — hair grows with the selected length */}
              <div className="relative w-14 shrink-0 flex flex-col items-center">
                <span className="w-7 h-7 rounded-full bg-[#e7d9bf] border border-[#d3c093]" />
                <div className="relative w-4 mt-1 rounded-b-full bg-[#e9e2d2]" style={{ height: 200 }}>
                  <motion.div
                    className="absolute top-0 left-0 right-0 rounded-b-full bg-gradient-to-b from-[#c6a15b] via-[#8a6a2c] to-[#4a463d]"
                    animate={{ height: `${pct * 100}%` }}
                    transition={{ type: 'spring', stiffness: 120, damping: 18 }}
                    data-testid="length-hair-fill"
                  />
                  {ZONES.map((z, i) => (
                    <span
                      key={z.label}
                      className="absolute left-1/2 -translate-x-1/2 w-3 h-px bg-[#faf6ef]/70"
                      style={{ top: `${((i + 1) / ZONES.length) * 100}%` }}
                    />
                  ))}
                </div>
              </div>

              {/* Zone list */}
              <ul className="flex-1 space-y-1.5">
                {ZONES.map((z) => {
                  const active = inches >= z.min && inches < z.max;
                  return (
                    <li
                      key={z.label}
                      className={`flex items-center justify-between px-3 py-1.5 text-sm font-body transition-colors ${active ? 'bg-[#0b0b0b] text-[#e7d3a1]' : 'text-[#4a463d]'}`}
                    >
                      <span className="flex items-center gap-2">
                        {active && <span className="w-1.5 h-1.5 rounded-full bg-[#c6a15b]" />}
                        {z.label}
                      </span>
                      <span className={active ? 'text-[#e7d3a1]' : 'text-[#8a8172]'}>{z.range}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            {lengths.length > 1 && onSelect && (
              <div className="mt-4 bg-[#faf6ef] border border-[#eadfca] p-4" data-testid="length-slider-wrap">
                <div className="flex items-center justify-between mb-2">
                  <span className="overline text-[0.6rem] text-[#a8823c]">Drag to grow your look</span>
                  <span className="font-display text-xl text-[#0b0b0b]" data-testid="length-slider-value">{selected}</span>
                </div>
                <input
                  type="range" min={0} max={lengths.length - 1} step={1} value={selIdx}
                  onChange={(e) => onSelect(lengths[Number(e.target.value)])}
                  className="w-full accent-[#c6a15b] cursor-pointer"
                  data-testid="length-slider" aria-label="Preview hair length"
                />
                <div className="flex justify-between font-body text-[0.62rem] text-[#8a8172] mt-1">
                  <span>{lengths[0]}</span>
                  <span>{lengths[lengths.length - 1]}</span>
                </div>
              </div>
            )}
            <p className="mt-2 font-body text-xs text-[#8a8172] italic">Falls where the hair reaches on an average 5'6" frame. Curly & wavy textures appear a little shorter when styled.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LengthGuide;
